import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LuArrowRight, LuCode, LuKeyRound, LuLayers, LuPencil, LuMessageSquare } from 'react-icons/lu';
import HeaderOne from '../../header/HeaderOne';
import FooterOne from '../../footer/FooterOne';
import Head from '../../components/Head';
import '../landing.css';

const TOOLS = [
  {
    icon: <LuCode size={18} strokeWidth={2} />,
    title: 'SMS API',
    body: 'Send SMS by REST from any backend. JS, Python, PHP, C# SDKs.',
    to: '/smsapi',
    cta: 'Read the API docs',
  },
  {
    icon: <LuKeyRound size={18} strokeWidth={2} />,
    title: 'Verification API',
    body: 'OTP and 2FA over SMS. We generate and match codes — no server-side storage required.',
    to: '/verificationapi',
    cta: 'See OTP flow',
  },
  {
    icon: <LuLayers size={18} strokeWidth={2} />,
    title: 'Bulk SMS API',
    body: 'Mass SMS via REST. Pass an array of messages, get a batch ID back.',
    to: '/bulksmsapi',
    cta: 'View bulk API',
  },
  {
    icon: <LuPencil size={18} strokeWidth={2} />,
    title: 'No-code Bulk SMS',
    body: 'CSV upload, message template with merge fields, live delivery dashboard.',
    to: '/bulksms',
    cta: 'Open the bulk sender',
  },
  {
    icon: <LuMessageSquare size={18} strokeWidth={2} />,
    title: 'Web Texting App',
    body: 'Browser-based SMS client. Two-way threads, shared contacts, multi-user inbox.',
    to: '/web-sms-app',
    cta: 'See the web app',
  },
];

export default function ToolsOverview() {
  const navigate = useNavigate();

  return (
    <>
      <Head
        title="SMS Tools — Dashboard, Bulk Sender, Web Texting"
        description="All TextLink SMS tools in one place: REST API, Bulk SMS API, OTP/Verification API, no-code bulk sender, and the web texting app. Pick the surface that fits your workflow."
        canonical="/smstoolsoverview"
      />
      <HeaderOne />

      <main className="lp">
        <div className="lp-shell">

          <header className="lp-hero">
            <span className="lp-eyebrow">Tools overview</span>
            <h1 className="lp-h1">
              Five ways to send.<br />
              <span className="lp-h1-accent">Same SIM, same dashboard.</span>
            </h1>
            <p className="lp-lead">
              Whether you're wiring SMS into a backend or running campaigns from a UI, TextLink has a tool that fits. All of them route through the same SIMs, billing, and inbox.
            </p>
            <div className="lp-cta-row">
              <button className="lp-btn lp-btn--primary" onClick={() => navigate('/signup')}>
                Try free <LuArrowRight size={15} strokeWidth={2} />
              </button>
              <Link className="lp-btn lp-btn--ghost" to="/pricing">See pricing</Link>
            </div>
          </header>

          <section className="lp-section">
            <h2 className="lp-h2">The toolkit</h2>
            <p className="lp-sub">Pick whichever surface matches the job.</p>
            <div className="lp-grid-2">
              {TOOLS.map((t, i) => (
                <Link key={i} to={t.to} className="lp-card">
                  <div className="lp-card-icon">{t.icon}</div>
                  <h3 className="lp-card-h">{t.title}</h3>
                  <p className="lp-card-p">{t.body}</p>
                  <span className="lp-card-link">
                    {t.cta} <LuArrowRight size={13} strokeWidth={2} />
                  </span>
                </Link>
              ))}
            </div>
          </section>

          <section className="lp-section">
            <h2 className="lp-h2">Pick by workflow</h2>
            <p className="lp-sub">Quick guidance if you're not sure which to start with.</p>

            <ul className="lp-feat-list">
              <li className="lp-feat">
                <div className="lp-feat-icon"><LuCode size={16} strokeWidth={2} /></div>
                <div className="lp-feat-text">
                  <strong>You have a backend</strong>
                  <span>Use the <Link to="/smsapi" style={{ color: 'inherit', textDecoration: 'underline' }}>SMS API</Link>. Three lines of curl, status webhooks back to your URL.</span>
                </div>
              </li>
              <li className="lp-feat">
                <div className="lp-feat-icon"><LuKeyRound size={16} strokeWidth={2} /></div>
                <div className="lp-feat-text">
                  <strong>You're building OTP / 2FA</strong>
                  <span>Use the <Link to="/verificationapi" style={{ color: 'inherit', textDecoration: 'underline' }}>Verification API</Link>. Two endpoints, no code storage.</span>
                </div>
              </li>
              <li className="lp-feat">
                <div className="lp-feat-icon"><LuPencil size={16} strokeWidth={2} /></div>
                <div className="lp-feat-text">
                  <strong>You run campaigns from spreadsheets</strong>
                  <span>Use <Link to="/bulksms" style={{ color: 'inherit', textDecoration: 'underline' }}>No-code Bulk SMS</Link>. CSV in, personalized SMS out.</span>
                </div>
              </li>
              <li className="lp-feat">
                <div className="lp-feat-icon"><LuMessageSquare size={16} strokeWidth={2} /></div>
                <div className="lp-feat-text">
                  <strong>You answer inbound replies all day</strong>
                  <span>Use the <Link to="/web-sms-app" style={{ color: 'inherit', textDecoration: 'underline' }}>Web Texting App</Link>. Browser inbox, real conversations.</span>
                </div>
              </li>
            </ul>
          </section>

          <section className="lp-cta">
            <h2 className="lp-h2">All five tools, one account.</h2>
            <p className="lp-sub">No add-on pricing — pick whichever you need today, switch surfaces later.</p>
            <div className="lp-cta-row">
              <button className="lp-btn lp-btn--primary" onClick={() => navigate('/signup')}>
                Try free <LuArrowRight size={15} strokeWidth={2} />
              </button>
              <Link className="lp-btn lp-btn--ghost" to="/pricing">See pricing</Link>
            </div>
          </section>

        </div>
      </main>

      <FooterOne />
    </>
  );
}
